import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { BarLoader, Button, CircularLoader, Input, Switch } from '@st-operations/uikit/src/v2'
import toast from '@st-operations/uikit/lib/toast'

import { createCleanroom, getCleanroom } from '_services/predactiv-admin'

const SHARING_ID_PLACEHOLDER =
  'aws:us-west-2:<metastore-uuid>:<workspace-id>:someone@theircompany.com'

const POLL_MS = 5000
// ~5 minutes of asking before falling back to the manual Refresh
const POLL_LIMIT = 60

const STATUS_LABEL = {
  creating: 'Creating',
  failed: 'Failed',
  ready: 'Ready',
}

/**
 * The organization's Databricks clean room. One per organization.
 *
 * Creating it is an action, not a field, so it sits outside the page's bulk save
 * — that fires on every edit, and a clean room must be created once.
 */
const CleanroomManager = ({ organizationId, env }) => {
  const [cleanroom, setCleanroom] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [sharingIdentifier, setSharingIdentifier] = useState('')
  const [hasPii, setHasPii] = useState(false)
  const [polls, setPolls] = useState(0)

  const fetchCleanroom = async () => {
    try {
      const response = await getCleanroom({ env, id: organizationId })
      setCleanroom(response?.cleanroom || null)
    } catch (error) {
      // quietly: this runs on page load, and most organizations have none
      setCleanroom((prev) => prev ?? null)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchCleanroom()
  }, [organizationId, env])

  // Each answer re-runs this effect and schedules the next ask, stopping once
  // the status settles. Capped so a page left open does not poll all afternoon.
  useEffect(() => {
    if (cleanroom?.status !== 'creating' || polls >= POLL_LIMIT) return undefined
    const timer = setTimeout(() => {
      setPolls((n) => n + 1)
      fetchCleanroom()
    }, POLL_MS)
    return () => clearTimeout(timer)
  }, [cleanroom, polls])

  const handleRefresh = () => {
    setPolls(0)
    fetchCleanroom()
  }

  const handleCreate = async () => {
    const trimmed = sharingIdentifier.trim()
    if (!trimmed) return

    setIsSubmitting(true)
    try {
      const response = await createCleanroom({
        env,
        id: organizationId,
        sharingIdentifier: trimmed,
        hasPii,
      })
      setCleanroom(response?.cleanroom || null)
      setSharingIdentifier('')
      setHasPii(false)
      // a retry starts its own polling window, not the failed attempt's
      setPolls(0)
      toast.success('Clean room requested')
    } catch (error) {
      toast.error(error.message || 'Error creating the clean room')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) return <BarLoader />

  const status = cleanroom?.status
  const isCreating = status === 'creating'
  // only a failed attempt is worth offering the form again for
  const showForm = !status || status === 'failed'

  // ⚠️ camelCase, not the snake_case the network tab shows: services/requests.js
  // camelizes every response, sparing only _-prefixed keys.
  const requested = cleanroom?.requestedAt
    ? dayjs(cleanroom.requestedAt).format('MMM D, YYYY h:mm A') +
      (cleanroom.requestedBy ? ` by ${cleanroom.requestedBy}` : '')
    : ''

  // absent on clean rooms requested before the flag existed
  const hasPiiLabel =
    typeof cleanroom?.hasPii === 'boolean' ? (cleanroom.hasPii ? 'Yes' : 'No') : ''

  const detailRows = [
    ['Name', cleanroom?.name],
    ['Sharing identifier', cleanroom?.sharingIdentifier],
    ['Has PII', hasPiiLabel],
    ['Requested', requested],
    ['Error', cleanroom?.error],
  ].filter(([, value]) => Boolean(value))

  return (
    <div>
      <div className="apa-org__permissions-block">
        <h4>Databricks Clean Room</h4>
      </div>

      <If condition={Boolean(status)}>
        <div className="flex flex-col gap-2 mt-5">
          <div className="flex items-center gap-2">
            <b>Status</b>
            <span>{STATUS_LABEL[status] || status}</span>
            <If condition={isCreating}>
              {/* 56px by default, and it takes no size prop */}
              <CircularLoader style={{ width: 14, height: 14, borderWidth: 2 }} />
            </If>
            <If condition={isCreating && polls >= POLL_LIMIT}>
              <Button onClick={handleRefresh}>Refresh</Button>
            </If>
          </div>
          {detailRows.map(([label, value]) => (
            <div key={label} className="flex items-start gap-2">
              <b className="shrink-0">{label}</b>
              <span className="break-all">{value}</span>
            </div>
          ))}
        </div>
      </If>

      <If condition={showForm}>
        <div className="flex flex-col gap-2 mt-5">
          <div className="apa-org__permission">
            <Switch
              checked={hasPii}
              onChange={(e) => {
                e.persist()
                setHasPii(e.target.checked)
              }}
            />
            Has PII
          </div>
          <div className="flex items-center gap-2 w-full">
            <Input
              className="w-full"
              label="Sharing identifier"
              placeholder={SHARING_ID_PLACEHOLDER}
              value={sharingIdentifier}
              onChange={(value) => setSharingIdentifier(value)}
            />
            <Button onClick={handleCreate} disabled={!sharingIdentifier.trim() || isSubmitting}>
              {status === 'failed' ? 'Try again' : 'Create Clean Room'}
            </Button>
          </div>
        </div>
      </If>
    </div>
  )
}

CleanroomManager.propTypes = {
  organizationId: PropTypes.string.isRequired,
  env: PropTypes.string.isRequired,
}

export default CleanroomManager
